import axios from "axios";
import API_ENDPOINTS from "../config/api";
import { useEffect, useState, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CircularProgress from "@mui/material/CircularProgress";
import DateRange from "../components/DateRange";

const Container = styled.div`
  min-height: 100dvh;
  width: 100%;
  padding: 2rem;
  display: flex;
  flex-direction: column;
`;

const ActivityBox = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const GoBack = styled.p`
  font-size: 1.4rem;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  color: #013566;
  margin-top: 2rem;
`;

const Tile = styled.div`
  border: 2px solid #eee;
  border-radius: 12px;
  -webkit-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  -moz-box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  box-shadow: 8px 8px 24px -15px rgba(66, 68, 90, 1);
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  width: 100%;

  h2 {
    font-size: 1.8rem;
  }
`;

const TileHeader = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  background: #f8f9fa;
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 1rem;
  border: 1px solid #eee;

  h2 {
    font-size: 1.2rem;
    width: 250px;
    font-weight: 600;
    color: #013566;
    margin: 0;
  }
`;

const TileSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  button {
    padding: 0.5rem;
    border: none;
    outline: none;
    border-radius: 12px;
    background-color: #013566;
    color: white;
  }
`;

const TileRow = styled.div`
  display: flex;
  justify-content: space-between;
  border-bottom: 1px solid #eee;
  gap: 1rem;
  padding: 12px 0;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
    border-radius: 4px;
    padding: 12px 8px;
    margin: 0 -8px;
  }

  &:last-child {
    border-bottom: none;
  }

  p {
    width: 250px;
  }
`;

const TileElement = styled.p`
  font-size: 1rem;
  font-weight: 500;
  color: #333;
  margin: 0;
`;

const TileCount = styled.p`
  font-size: 1.2rem;
  font-weight: 600;
  color: #013566;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;

  svg {
    width: 16px;
    color: #013566;
  }
`;

const CampaignIdSelect = styled.div`
  margin-bottom: 1.5rem;

  p {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #013566;
  }

  select {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 200px;

    &:hover {
      border-color: #013566;
    }

    &:focus {
      outline: none;
      border-color: #013566;
      box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
    }
  }
`;

const FormTypeSelect = styled.div`
  margin-bottom: 1.5rem;

  p {
    font-size: 0.9rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #013566;
  }

  select {
    padding: 0.6rem 0.8rem;
    font-size: 0.9rem;
    border: 1px solid #ddd;
    border-radius: 8px;
    background: white;
    color: #333;
    cursor: pointer;
    transition: all 0.2s ease;
    min-width: 200px;

    &:hover {
      border-color: #013566;
    }

    &:focus {
      outline: none;
      border-color: #013566;
      box-shadow: 0 0 0 2px rgba(1, 53, 102, 0.2);
    }
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin: 2rem 0;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #eee;
`;

const PaginationButton = styled.button<{ $disabled?: boolean }>`
  padding: 0.5rem 1rem;
  border: 1px solid #013566;
  border-radius: 6px;
  background: ${(props) => (props.$disabled ? "#f8f9fa" : "#013566")};
  color: ${(props) => (props.$disabled ? "#6c757d" : "white")};
  cursor: ${(props) => (props.$disabled ? "not-allowed" : "pointer")};
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #012a4a;
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const PageInfo = styled.span`
  font-weight: 500;
  color: #013566;
  font-size: 0.9rem;
`;

const ConversionSection = styled.div`
  margin-top: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #013566 0%, #012a4a 100%);
  border-radius: 8px;
  color: white;
`;

const ConversionTitle = styled.h3`
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  color: white;
`;

const ConversionValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #fff;
`;

interface Props {
  startDate: Date | null;
  endDate: Date | null;
  handleStartDateChange: (date: Date | null) => void;
  handleEndDateChange: (date: Date | null) => void;
}

const Sweeps: React.FC<Props> = ({
  startDate,
  endDate,
  handleEndDateChange,
  handleStartDateChange,
}) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedFormType, setSelectedFormType] = useState<string>("all");
  const itemsPerPage = 10;

  const token = localStorage.getItem("token");

  useEffect(() => {
    if (!token) {
      navigate("/dashboard");
    }
  }, [token]);

  const fetchStats = async () => {
    setLoading(true);

    const formattedStartDate = startDate
      ? startDate.toISOString().split("T")[0]
      : "";
    const formattedEndDate = endDate
      ? endDate.toISOString().split("T")[0]
      : new Date().toISOString().split("T")[0];

    try {
      const response = await axios.get(API_ENDPOINTS.SWEEPS_DETAILS, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: {
          start_date: formattedStartDate,
          end_date: formattedEndDate,
        },
      });
      setStats(response.data);
    } catch (error: any) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, [token, startDate, endDate]);

  // Get unique form types for the select dropdown
  const formTypes = useMemo(() => {
    if (!stats?.form_submissions) return [];
    const types = Array.from(
      new Set(
        stats.form_submissions.map(
          (submission: any) => submission?.form_type || "Unknown"
        )
      )
    ) as string[];
    return types.sort();
  }, [stats?.form_submissions]);

  // Get count breakdown by form type
  const formTypeCounts = useMemo(() => {
    if (!stats?.form_submissions) return {};
    const counts: { [key: string]: number } = {};
    stats.form_submissions.forEach((submission: any) => {
      const formType = submission?.form_type || "Unknown";
      counts[formType] = (counts[formType] || 0) + 1;
    });
    return counts;
  }, [stats?.form_submissions]);

  // Filter submissions by selected form type
  const filteredSubmissions = useMemo(() => {
    if (!stats?.form_submissions) return [];
    if (selectedFormType === "all") return stats.form_submissions;
    return stats.form_submissions.filter(
      (submission: any) => submission?.form_type === selectedFormType
    );
  }, [stats?.form_submissions, selectedFormType]);

  // Calculate pagination
  const totalSubmissions = filteredSubmissions.length;
  const totalPages = Math.max(1, Math.ceil(totalSubmissions / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSubmissions = filteredSubmissions.slice(startIndex, endIndex);

  // Reset to first page when data changes or form type changes
  useEffect(() => {
    setCurrentPage(1);
  }, [stats?.form_submissions, selectedFormType]);

  // Ensure current page is valid when data changes
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  return (
    <Container>
      <DateRange
        startDate={startDate}
        endDate={endDate}
        handleEndDateChange={handleEndDateChange}
        handleStartDateChange={handleStartDateChange}
      />

      {loading ? (
        <ActivityBox>
          <CircularProgress />
        </ActivityBox>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "2rem",
            marginTop: "2rem",
          }}
        >
          <h1>Sweepstakes</h1>
          <Tile>
            <h2>Page Views</h2>
            <TileHeader>
              <h2>Views</h2>
              <h2>Count</h2>
            </TileHeader>

            <TileSection>
              {stats?.page_views?.total_views > 0 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                  }}
                >
                  <TileRow>
                    <TileElement>Total Views</TileElement>
                    <TileCount>{stats?.page_views?.total_views}</TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Mobile Views</TileElement>
                    <TileCount>
                      {stats?.page_views?.devices[0]?.total}
                    </TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Desktop Views</TileElement>
                    <TileCount>
                      {stats?.page_views?.devices[1]?.total}
                    </TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Tablet Views</TileElement>
                    <TileCount>
                      {stats?.page_views?.devices[2]?.total}
                    </TileCount>
                  </TileRow>
                  <TileRow>
                    <TileElement>Unique Views</TileElement>
                    <TileCount>{stats?.page_views?.unique_views}</TileCount>
                  </TileRow>
                </div>
              ) : (
                <p>No page view data available</p>
              )}
            </TileSection>
          </Tile>

          <Tile>
            <h2>Clicks</h2>
            <TileHeader>
              <h2>Clicked Element</h2>
              <h2>Count</h2>
            </TileHeader>

            <TileSection>
              {/* <TileRow>
                <TileElement>Unique Clicks</TileElement>
                <TileCount>{stats?.clicks?.unique_clicks}</TileCount>
              </TileRow> */}
              {stats?.clicks?.all?.length > 0 ? (
                stats.clicks.all.map((click: any, index: number) => (
                  <TileRow key={index}>
                    <TileElement>{click?.element}</TileElement>
                    <TileCount>{click?.total_clicks}</TileCount>
                  </TileRow>
                ))
              ) : (
                <p>No click data available.</p>
              )}
            </TileSection>
          </Tile>

          <Tile>
            <h2>
              Form Submissions (Total: {filteredSubmissions.length})
              {Object.keys(formTypeCounts).length > 0 && (
                <span
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "black",
                    marginLeft: "1rem",
                  }}
                >
                  -{" "}
                  {Object.entries(formTypeCounts)
                    .map(([type, count]) => `${type}: ${count}`)
                    .join(", ")}
                </span>
              )}
            </h2>

            <FormTypeSelect>
              <p>Filter by Form Type</p>
              <select
                value={selectedFormType}
                onChange={(e) => setSelectedFormType(e.target.value)}
              >
                <option value="all">All Form Types</option>
                {formTypes.map((formType: string) => (
                  <option key={formType} value={formType}>
                    {formType}
                  </option>
                ))}
              </select>
            </FormTypeSelect>

            <TileHeader>
              <h2>Submission Date</h2>
              <h2>Form Type</h2>
            </TileHeader>

            <TileSection>
              {stats?.form_submissions?.length > 0 ? (
                <>
                  {currentSubmissions.map((submission: any, index: number) => (
                    <TileRow key={index}>
                      <TileElement>
                        {submission?.created_at
                          ? new Date(submission.created_at).toLocaleString(
                              "en-US",
                              {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              }
                            )
                          : "No Date Provided"}
                      </TileElement>
                      <TileElement>
                        {submission?.form_type || "No Form Type Provided"}
                      </TileElement>
                    </TileRow>
                  ))}

                  {/* Pagination Controls */}
                  <PaginationContainer>
                    <PaginationButton
                      $disabled={currentPage <= 1}
                      onClick={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                    >
                      ← Previous
                    </PaginationButton>
                    <PageInfo>
                      Page {currentPage} of {totalPages}
                    </PageInfo>
                    <PaginationButton
                      $disabled={currentPage >= totalPages}
                      onClick={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                    >
                      Next →
                    </PaginationButton>
                  </PaginationContainer>

                  {/* <ConversionSection>
                    <ConversionTitle>
                      Conversions - engaged website visitors / total visitors
                    </ConversionTitle>
                    <div
                      style={{
                        fontSize: "0.8rem",
                        opacity: 0.8,
                        marginBottom: "0.5rem",
                      }}
                    >
                      Data flow starts 06.02.2025
                    </div>
                    <ConversionValue>
                      {stats?.page_views?.conversions}%
                    </ConversionValue>
                  </ConversionSection> */}
                </>
              ) : (
                <p>No form submissions available.</p>
              )}
            </TileSection>
          </Tile>
        </div>
      )}

      <GoBack onClick={() => navigate(-1)}>Go Back</GoBack>
    </Container>
  );
};

export default Sweeps;
